import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Card, Row, Col, Typography, Spin, message, Tag, Button, Modal } from 'antd';
import {
	CalendarOutlined,
	LeftOutlined,
	RightOutlined,
	CheckCircleOutlined,
	EyeOutlined,
} from '@ant-design/icons';
import styled from 'styled-components';
import moment from 'moment';
import { Box } from 'atoms';
import { fetchFinanceRequestDetail, fetchFinanceRequestNavigation } from 'store/financeSlice';
import { commonUpdate, getSignedUrl } from 'store/commonSlice';
import {
	fetchFinanceRequestDetailV2,
	fetchFinanceRequestNavigationV2,
	markForPaymentV2,
	getSignedUrlV2,
} from 'store/financeSliceV2';
import { getUIVersion } from 'utils/router-static-data';

const { Text } = Typography;

const PageContainer = styled(Box)`
	padding: 24px;
	background: #f5f5f5;
	min-height: calc(100vh - 64px);
`;

const HeaderContainer = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 24px;
	padding: 16px 24px;
	background: #ffffff;
	border-radius: 8px;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const RequestInfo = styled.div`
	display: flex;
	flex-direction: row;
	align-items: center;
	gap: 8px;
`;

const RequestLabel = styled(Text)`
	font-size: 14px;
	color: #666;
	white-space: nowrap;
`;

const RequestValue = styled(Text)`
	font-size: 16px;
	color: #000;
	font-weight: 600;
	white-space: nowrap;
`;

const DetailsCard = styled(Card)`
	border-radius: 8px;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
	margin-bottom: 24px;
`;

const FieldLabel = styled(Text)`
	font-size: 12px;
	color: #666;
	display: block;
	margin-bottom: 4px;
`;

const FieldValue = styled(Text)`
	font-size: 14px;
	color: #000;
	font-weight: 500;
	display: block;
`;

const StatusBadge = styled(Tag)`
	padding: 4px 12px;
	border-radius: 4px;
	font-size: 12px;
	font-weight: 500;
	border: none;
	margin: 0;
`;

const ButtonContainer = styled.div`
	display: flex;
	justify-content: center;
	gap: 16px;
	margin-top: 24px;
	padding: 24px;
	background: #ffffff;
	border-radius: 8px;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const PreviousButton = styled(Button)`
	border: 1px solid #d9d9d9;
	color: #8c8c8c;
	background: #fafafa;
	display: flex;
	align-items: center;
	gap: 8px;

	&:hover {
		border-color: #18405d;
		color: #18405d;
		background: #fafafa;
	}
`;

const MarkToPayButton = styled(Button)`
	background: #18405d;
	border-color: #18405d;
	color: #ffffff;
	font-weight: 500;

	&:hover {
		background: #18405d;
		border-color: #18405d;
		color: #ffffff;
	}
`;

const SkipButton = styled(Button)`
	border: 1px solid #18405d;
	color: #18405d;
	background: #ffffff;
	display: flex;
	align-items: center;
	gap: 8px;

	&:hover {
		border-color: #18405d;
		color: #18405d;
		background: #ffffff;
	}
`;

const getStatusBadgeColor = (status) => {
	if (!status) return 'default';
	const statusLower = status.toLowerCase();
	if (statusLower.includes('approved') || statusLower.includes('marked')) {
		return 'success';
	}
	if (statusLower.includes('pending')) {
		return 'warning';
	}
	if (statusLower.includes('rejected')) {
		return 'error';
	}
	return 'default';
};

const formatCurrency = (value) => {
	if (!value) return '₹ 0';
	return `₹ ${parseFloat(value).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
};

const formatPercentage = (value, total) => {
	if (!value || !total || parseFloat(total) === 0) return '0%';
	const percentage = ((parseFloat(value) / parseFloat(total)) * 100).toFixed(0);
	return `${percentage}%`;
};

const REQUEST_QUEUE_TAB = 'request-queue';

// eslint-disable-next-line sonarjs/cognitive-complexity
function FinanceRequestDetails() {
	const { id } = useParams();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const dispatch = useDispatch();
	const isV2 = useMemo(() => getUIVersion() === 'new', []);
	const [loading, setLoading] = useState(true);
	const [details, setDetails] = useState(null);
	const [previousId, setPreviousId] = useState(null);
	const [nextId, setNextId] = useState(null);
	const [markingToPay, setMarkingToPay] = useState(false);
	const [successModalVisible, setSuccessModalVisible] = useState(false);
	const [previewVisible, setPreviewVisible] = useState(false);
	const [previewUrl, setPreviewUrl] = useState('');
	const [previewTitle, setPreviewTitle] = useState('');
	const [previewLoading, setPreviewLoading] = useState(false);
	const sourceTab = searchParams.get('sourceTab') || REQUEST_QUEUE_TAB;

	useEffect(() => {
		if (id) {
			fetchDetails();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [id]);

	// Fetch navigation data when coming from request-queue tab
	useEffect(() => {
		if (sourceTab === REQUEST_QUEUE_TAB && id) {
			fetchNavigation();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [sourceTab, id]);

	const fetchNavigation = async () => {
		try {
			const requestId = id;
			if (!requestId) return;

			if (isV2) {
				const response = await dispatch(
					fetchFinanceRequestNavigationV2({
						id: requestId,
					})
				);
				// V2 response: { finance_request: {...}, navigation: { previous_request_id, next_request_id } }
				if (response) {
					setPreviousId(response.navigation?.previous_request_id || null);
					setNextId(response.navigation?.next_request_id || null);
				}
			} else {
				const response = await dispatch(
					fetchFinanceRequestNavigation({
						current_request_id: requestId,
					})
				);
				if (response) {
					setPreviousId(response.previous_id);
					setNextId(response.next_id);
				}
			}
		} catch (error) {
			console.error('Error fetching navigation:', error);
		}
	};

	const fetchDetails = async () => {
		setLoading(true);
		try {
			const action = isV2
				? fetchFinanceRequestDetailV2({ id })
				: fetchFinanceRequestDetail({ id });
			const response = await dispatch(action);

			if (response?.rows && response.rows.length > 0) {
				setDetails(response.rows[0]);
			} else {
				message.error('Request not found');
				navigate(`/finance/requests?tab=${sourceTab}`);
			}
		} catch (error) {
			console.error('Error fetching details:', error);
			message.error('Failed to fetch request details');
		} finally {
			setLoading(false);
		}
	};

	// Format date with time - DD-MM-YYYY h:mm a
	const formatDateOnly = (dateString) => {
		if (!dateString) return '-';
		try {
			return moment(dateString).format('DD-MM-YYYY');
		} catch (error) {
			return dateString;
		}
	};

	// Format location: pincode, city, state, state_abbrev
	const formatLocation = (pincode, city, state) => {
		if (!pincode && !city && !state) return '-';
		const parts = [];
		if (pincode) parts.push(pincode);
		if (city) parts.push(city);
		if (state) {
			parts.push(state);
			// Get state abbreviation (first letter of each word, or first 2-3 letters)
			const words = state.split(' ');
			const stateAbbrev =
				words.length > 1
					? words
							.map((w) => w[0])
							.join('.')
							.toUpperCase()
					: state.substring(0, 3).toUpperCase();
			parts.push(stateAbbrev);
		}
		return parts.join(', ');
	};

	const handlePrevious = () => {
		if (previousId) {
			navigate(`/finance/requests/${previousId}?sourceTab=${sourceTab}`);
		}
	};

	const handleNext = () => {
		if (nextId) {
			navigate(`/finance/requests/${nextId}?sourceTab=${sourceTab}`);
		}
	};

	const handleMarkToPay = async () => {
		if (!details?.id && !details?.adhoc_request_id) {
			message.error('Request ID not found');
			return;
		}

		setMarkingToPay(true);
		try {
			const requestId = details.id || details.adhoc_request_id;
			if (isV2) {
				await dispatch(markForPaymentV2({ finance_request_id: requestId }));
			} else {
				await dispatch(
					commonUpdate({
						update_type: 'finance_request',
						data: {
							id: requestId,
							status: 'Marked',
						},
					})
				);
			}

			setSuccessModalVisible(true);
			// Refresh details after marking
			await fetchDetails();
		} catch (error) {
			console.error('Error marking to pay:', error);
			message.error('Failed to mark request to pay');
		} finally {
			setMarkingToPay(false);
		}
	};

	const handleSuccessModalClose = () => {
		setSuccessModalVisible(false);
		// Navigate to next request if available
		if (nextId) {
			handleNext();
		}
	};

	const getFilenameFromUrl = (url) => {
		if (!url) return '';
		return url.startsWith('s3://') ? url.replace('s3://logitrack/', '') : url;
	};

	const getFileExtension = (filename) => {
		if (!filename) return '';
		return filename.split('.').pop().toLowerCase();
	};

	const handlePreview = async (url, title) => {
		if (!url) {
			message.error('Document not available');
			return;
		}
		if (title) {
			setPreviewTitle(title);
		}
		setPreviewLoading(true);
		try {
			const filename = getFilenameFromUrl(url);
			const data = {
				url_type: 'view',
				data: {
					entity: 'trip',
					keys: [filename],
				},
			};

			const signedUrlAction = isV2 ? getSignedUrlV2 : getSignedUrl;
			const response = await dispatch(signedUrlAction(data));
			if (response?.data && response.data[0]?.url) {
				setPreviewUrl(response.data[0].url);
				setPreviewTitle(filename);
				setPreviewVisible(true);
			} else {
				message.error('Failed to get preview URL');
			}
		} catch (error) {
			console.error('Preview error:', error);
			message.error('Failed to preview document');
		} finally {
			setPreviewLoading(false);
		}
	};

	const renderPreviewContent = () => {
		const fileExt = getFileExtension(previewTitle);

		if (['jpg', 'jpeg', 'png', 'gif'].includes(fileExt)) {
			return (
				<img
					alt={previewTitle}
					style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
					src={previewUrl}
				/>
			);
		}
		if (fileExt === 'pdf') {
			return (
				<div style={{ textAlign: 'center', padding: '20px' }}>
					<div style={{ marginBottom: '20px' }}>
						<p style={{ fontSize: '16px', marginBottom: '10px' }}>
							PDF Preview: {previewTitle}
						</p>
						<p style={{ color: '#666', marginBottom: '20px' }}>
							For better PDF viewing experience, please open in a new tab
						</p>
					</div>
					<div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
						<Button
							type="primary"
							onClick={() => window.open(previewUrl, '_blank')}
							size="large"
						>
							Open PDF in New Tab
						</Button>
					</div>
					<div
						style={{
							marginTop: '20px',
							border: '1px solid #d9d9d9',
							borderRadius: '4px',
						}}
					>
						<iframe
							title={previewTitle}
							src={`${previewUrl}#toolbar=0&navpanes=0&scrollbar=0`}
							width="100%"
							height="400px"
							style={{ border: 'none' }}
						/>
					</div>
				</div>
			);
		}
		return (
			<div style={{ textAlign: 'center', padding: '20px' }}>
				<p>Preview not available for this file type.</p>
				<Button type="primary" onClick={() => window.open(previewUrl, '_blank')}>
					Open in New Tab
				</Button>
			</div>
		);
	};

	if (loading) {
		return (
			<PageContainer>
				<Spin size="large" style={{ display: 'block', margin: '100px auto' }} />
			</PageContainer>
		);
	}

	if (!details) {
		return (
			<PageContainer>
				<Text>Request not found</Text>
			</PageContainer>
		);
	}

	// Format request number (using adhoc_request_id if available, otherwise id)
	const requestId = details.adhoc_request_id;
	const requestNumber = details.id;

	return (
		<PageContainer>
			<HeaderContainer>
				<RequestInfo>
					<RequestLabel>Request No</RequestLabel>
					<RequestValue>{requestNumber}</RequestValue>
				</RequestInfo>
				<RequestInfo>
					<RequestLabel>Status</RequestLabel>
					<StatusBadge color={getStatusBadgeColor(details.status)}>
						{sourceTab === REQUEST_QUEUE_TAB
							? details.payment_status || 'Unmarked'
							: details.status || '-'}
					</StatusBadge>
				</RequestInfo>
				<RequestInfo>
					<RequestLabel>Adhoc Request ID</RequestLabel>
					<RequestValue>{requestId}</RequestValue>
				</RequestInfo>
				<RequestInfo>
					<RequestLabel>Date</RequestLabel>
					<div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
						<CalendarOutlined style={{ fontSize: '14px', color: '#666' }} />
						<RequestValue>{formatDateOnly(details.request_date) || '-'}</RequestValue>
					</div>
				</RequestInfo>
			</HeaderContainer>

			{/* Card 1: Request Overview */}
			<DetailsCard>
				<Row gutter={[24, 16]}>
					<Col span={8}>
						<FieldLabel>Client</FieldLabel>
						<FieldValue>{details.client_name || '-'}</FieldValue>
					</Col>
					<Col span={8}>
						<FieldLabel>Project</FieldLabel>
						<FieldValue>{details.project_name || '-'}</FieldValue>
					</Col>
					<Col span={8}>
						<FieldLabel>Vendor</FieldLabel>
						<FieldValue>
							{details.party_name || details.vendor_info?.vendor_name || '-'}
						</FieldValue>
					</Col>
				</Row>
				<Row gutter={[24, 16]} style={{ marginTop: 16 }}>
					<Col span={8}>
						<FieldLabel>Vehicle Type</FieldLabel>
						<FieldValue>{details.vehicle_type || '-'}</FieldValue>
					</Col>
					<Col span={8}>
						<FieldLabel>Source Location*</FieldLabel>
						<FieldValue>
							{formatLocation(
								details.source_pincode,
								details.source_city,
								details.source_state
							)}
						</FieldValue>
					</Col>
					<Col span={8}>
						<FieldLabel>Destination Location*</FieldLabel>
						<FieldValue>
							{formatLocation(
								details.destination_pincode,
								details.destination_city,
								details.destination_state
							)}
						</FieldValue>
					</Col>
				</Row>
				<Row gutter={[24, 16]} style={{ marginTop: 16 }}>
					<Col span={8}>
						<FieldLabel>Trip Date</FieldLabel>
						<FieldValue>{formatDateOnly(details.expected_trip_date) || '-'}</FieldValue>
					</Col>
					<Col span={8}>
						<FieldLabel>Temperature Log*</FieldLabel>
						<FieldValue>
							{details.is_temperature_log_required ? 'Yes Required' : 'No'}
						</FieldValue>
					</Col>
					<Col span={8}>
						<FieldLabel>Business Unit*</FieldLabel>
						<FieldValue>{details.business_unit_name || '-'}</FieldValue>
					</Col>
				</Row>
				<Row gutter={[24, 16]} style={{ marginTop: 16 }}>
					<Col span={8}>
						<FieldLabel>Supervisor Name*</FieldLabel>
						<FieldValue>{details.supervisor_name || '-'}</FieldValue>
					</Col>
					<Col span={8}>
						<FieldLabel>Request Status*</FieldLabel>
						<FieldValue>
							<span
								style={{
									display: 'inline-flex',
									alignItems: 'center',
									gap: '8px',
									color: '#52c41a',
								}}
							>
								<span
									style={{
										width: '8px',
										height: '8px',
										borderRadius: '50%',
										backgroundColor: '#52c41a',
										display: 'inline-block',
									}}
								/>{' '}
								Approved
							</span>
						</FieldValue>
					</Col>
					<Col span={8}>
						<FieldLabel>Advance Status*</FieldLabel>
						<FieldValue>
							<StatusBadge color={getStatusBadgeColor(details.advance_status)}>
								{details.advance_status || 'Pending'}
							</StatusBadge>
						</FieldValue>
					</Col>
				</Row>
			</DetailsCard>

			{/* Card 2: Financial and Movement Details */}
			<DetailsCard>
				<Row gutter={[24, 16]}>
					<Col span={8}>
						<FieldLabel>Trip ID</FieldLabel>
						<FieldValue style={{ color: '#52c41a' }}>
							{details.trip_id || '-'}
						</FieldValue>
					</Col>
					<Col span={8}>
						<FieldLabel>Movement Type</FieldLabel>
						<FieldValue>{details.movement_type || '-'}</FieldValue>
					</Col>
					<Col span={8}>
						<FieldLabel>Client Rate</FieldLabel>
						<FieldValue>{formatCurrency(details.client_rate)}</FieldValue>
					</Col>
				</Row>
				<Row gutter={[24, 16]} style={{ marginTop: 16 }}>
					<Col span={8}>
						<FieldLabel>Margin</FieldLabel>
						<FieldValue>
							{formatCurrency(details.margin)} (
							{formatPercentage(details.margin, details.client_rate)})
						</FieldValue>
					</Col>
					<Col span={8}>
						<FieldLabel>Advance</FieldLabel>
						<FieldValue>
							{formatCurrency(
								details.advance_amount_requested || details.request_value
							)}{' '}
							(
							{formatPercentage(
								details.advance_amount_requested || details.request_value,
								details.vendor_rate
							)}
							)
						</FieldValue>
					</Col>
					<Col span={8}>
						<FieldLabel>Vendor Rate</FieldLabel>
						<FieldValue>{formatCurrency(details.vendor_rate)}</FieldValue>
					</Col>
				</Row>
			</DetailsCard>

			{/* Card 3: Payment and Rejection Information + Supporting Documents */}
			<DetailsCard>
				{sourceTab !== REQUEST_QUEUE_TAB && (
					<Row gutter={[24, 16]}>
						<Col span={8}>
							<FieldLabel>Payment details</FieldLabel>
							<FieldValue>{details.payment_status || '-'}</FieldValue>
						</Col>
						<Col span={8}>
							<FieldLabel>Rejected Details</FieldLabel>
							<FieldValue>{details.rejected_details || '-'}</FieldValue>
						</Col>
						<Col span={8}>
							<FieldLabel>Rejected Reason</FieldLabel>
							<FieldValue>{details.rejected_remarks || '-'}</FieldValue>
						</Col>
					</Row>
				)}
				<Row gutter={[24, 12]} style={{ marginTop: 24 }}>
					<Col span={8}>
						<FieldLabel>Proof Of Request</FieldLabel>
						{details.proof_of_request_url ? (
							<Button
								type="primary"
								icon={<EyeOutlined />}
								onClick={() =>
									handlePreview(details.proof_of_request_url, 'Proof of Request')
								}
								loading={previewLoading}
								style={{
									marginTop: 8,
									background: '#18405d',
									borderColor: '#18405d',
								}}
							>
								Preview Document
							</Button>
						) : (
							<Text style={{ marginTop: 8, display: 'block' }}>
								No document uploaded
							</Text>
						)}
					</Col>
					<Col span={8}>
						<FieldLabel>Pro-forma Invoice</FieldLabel>
						{details.vendor_proforma_invoice_url ? (
							<Button
								type="primary"
								icon={<EyeOutlined />}
								onClick={() =>
									handlePreview(
										details.vendor_proforma_invoice_url,
										'Pro-Forma Invoice'
									)
								}
								loading={previewLoading}
								style={{
									marginTop: 8,
									background: '#18405d',
									borderColor: '#18405d',
								}}
							>
								Preview Document
							</Button>
						) : (
							<Text style={{ marginTop: 8, display: 'block' }}>
								No document uploaded
							</Text>
						)}
					</Col>
					<Col span={1}>
						<Text
							style={{
								fontSize: 12,
								marginTop: 8,
								whiteSpace: 'nowrap',
							}}
						>
							Pro-Forma Invoice Number:{' '}
							<div style={{ fontSize: 12, fontWeight: 'bold' }}>
								{details.vendor_proforma_invoice_number
									? details.vendor_proforma_invoice_number
									: '-'}
							</div>
						</Text>
					</Col>
				</Row>
			</DetailsCard>

			{/* Action Buttons - Only show when coming from request-queue */}
			{sourceTab === REQUEST_QUEUE_TAB && (
				<ButtonContainer>
					<PreviousButton
						icon={<LeftOutlined />}
						onClick={handlePrevious}
						disabled={!previousId}
					>
						Previous
					</PreviousButton>
					<MarkToPayButton
						type="primary"
						onClick={handleMarkToPay}
						loading={markingToPay}
						disabled={markingToPay || details?.status === 'Marked'}
					>
						Mark to Pay
					</MarkToPayButton>
					<SkipButton icon={<RightOutlined />} onClick={handleNext} disabled={!nextId}>
						Skip
					</SkipButton>
				</ButtonContainer>
			)}

			{/* Success Modal */}
			<Modal
				open={successModalVisible}
				onCancel={handleSuccessModalClose}
				footer={[
					<Button key="ok" type="primary" onClick={handleSuccessModalClose}>
						OK
					</Button>,
				]}
				centered
				width={400}
			>
				<div style={{ textAlign: 'center', padding: '24px 0' }}>
					<CheckCircleOutlined
						style={{
							fontSize: '64px',
							color: '#52c41a',
							marginBottom: '16px',
						}}
					/>
					<Typography.Title level={4} style={{ margin: 0 }}>
						Successfully marked
					</Typography.Title>
				</div>
			</Modal>

			{/* Preview Modal */}
			<Modal
				open={previewVisible}
				title={previewTitle}
				footer={null}
				onCancel={() => {
					setPreviewVisible(false);
					setPreviewUrl('');
					setPreviewTitle('');
				}}
				centered
				width={getFileExtension(previewTitle) === 'pdf' ? 1000 : 800}
				bodyStyle={{ textAlign: 'center', background: '#fff' }}
				destroyOnClose
			>
				{renderPreviewContent()}
			</Modal>
		</PageContainer>
	);
}

export default FinanceRequestDetails;
